import { html, render } from 'lit-html';
import { appStore, actions } from '../store/appStore.js';
import checkIcon from '../icons/check.svg';
import closeIcon from '../icons/close.svg';
import errorIcon from '../icons/error.svg';

export class Alert extends HTMLElement {
  private unsubscribe?: () => void;
  private autoCloseTimeouts: Map<string, number> = new Map();

  connectedCallback() {
    this.unsubscribe = appStore.subscribe(() => {
      this.render();
    });
    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    this.autoCloseTimeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
    this.autoCloseTimeouts.clear();
  }

  private handleDismiss = (alertId: string) => {
    actions.removeAlert(alertId);
    const timeoutId = this.autoCloseTimeouts.get(alertId);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      this.autoCloseTimeouts.delete(alertId);
    }
  };

  private getAlertConfig(type: string) {
    switch (type) {
      case 'success':
        return {
          bgClass: 'bg-emerald-500',
          textClass: 'text-white',
          hoverClass: 'hover:text-gray-200',
          icon: checkIcon,
          iconAlt: 'Success'
        };
      case 'error':
        return {
          bgClass: 'bg-red-500',
          textClass: 'text-white',
          hoverClass: 'hover:text-gray-200',
          icon: errorIcon,
          iconAlt: 'Error'
        };
      case 'warning':
        return {
          bgClass: 'bg-orange-500',
          textClass: 'text-white',
          hoverClass: 'hover:text-gray-200',
          icon: closeIcon,
          iconAlt: 'Warning'
        };
      default:
        return {
          bgClass: 'bg-emerald-500',
          textClass: 'text-white',
          hoverClass: 'hover:text-gray-200',
          icon: checkIcon,
          iconAlt: 'Success'
        };
    }
  }

  private render() {
    const state = appStore.getState();
    const scope = this.getAttribute('scope') || 'global';

    // Only show alerts that match this component's scope
    const scopedAlerts = state.alerts.filter(alert => alert.scope === scope);

    if (scopedAlerts.length === 0) {
      render(html``, this);
      return;
    }

    // Set up auto-close timers for new alerts
    scopedAlerts.forEach(alert => {
      if (!this.autoCloseTimeouts.has(alert.id)) {
        const timeoutId = window.setTimeout(() => {
          actions.removeAlert(alert.id);
          this.autoCloseTimeouts.delete(alert.id);
        }, 10000);
        this.autoCloseTimeouts.set(alert.id, timeoutId);
      }
    });

    // Clean up timers for alerts that no longer exist
    this.autoCloseTimeouts.forEach((timeoutId, alertId) => {
      if (!scopedAlerts.find(alert => alert.id === alertId)) {
        window.clearTimeout(timeoutId);
        this.autoCloseTimeouts.delete(alertId);
      }
    });

    // Add fixed positioning for global alerts
    const containerClasses = scope === 'global' ?
      'fixed top-0 left-0 right-0 z-50' : '';

    const template = html`
      <div class="${containerClasses} space-y-2">
        ${scopedAlerts.map(alert => {
          const config = this.getAlertConfig(alert.type);
          return html`
            <div class="${config.bgClass} ${config.textClass} p-4 flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <img src="${config.icon}" class="w-5 h-5" alt="${config.iconAlt}" />
                <span class="font-medium">${alert.message}</span>
              </div>
              <button
                @click=${() => this.handleDismiss(alert.id)}
                class="${config.textClass} ${config.hoverClass}"
              >
                <img src="${closeIcon}" class="w-5 h-5 filter brightness-0 invert" alt="Close" />
              </button>
            </div>
          `;
        })}
      </div>
    `;

    render(template, this);
  }
}

customElements.define('alert-component', Alert);
